import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Download, Ban, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pdfAPI, adminAPI, clientAPI, billingAPI, documentAPI } from "@/lib/api";
import { EmailSendDialog } from "@/components/EmailSendDialog";
import { invoiceRecapText, invoiceRestePayer, appendRecapToTemplate } from "@/lib/emailRecap";

const formatEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

function buildEmailMessage(doc, client, settings) {
  const contactName = `${client?.first_name || ""} ${client?.last_name || ""}`.trim() || client?.contact_name || "Madame, Monsieur";
  const clientCompany = client?.company || client?.nom_etablissement || "";
  const coName = settings?.name || "Notre société";
  const docDate = doc?.date ? new Date(doc.date).toLocaleDateString("fr-FR") : "";
  const docLabel = "facture";
  const template = settings?.email_template;
  if (template) {
    const rendered = template
      .replace(/\{client_name\}/g, contactName)
      .replace(/\{doc_label\}/g, docLabel)
      .replace(/\{doc_number\}/g, doc?.number || "")
      .replace(/\{doc_date\}/g, docDate)
      .replace(/\{total_ttc\}/g, formatEur(doc?.total_ttc))
      .replace(/\{reste_a_payer\}/g, invoiceRestePayer(doc))
      .replace(/\{company_name\}/g, coName)
      .replace(/\{client_company\}/g, clientCompany);
    return appendRecapToTemplate(template, rendered, doc);
  }
  const amountBlock = invoiceRecapText(doc) || `Montant TTC : ${formatEur(doc?.total_ttc)}`;
  return `Bonjour ${contactName},\n\nVeuillez trouver ci-joint votre ${docLabel} ${doc?.number || ""} du ${docDate}${clientCompany ? ` pour la société ${clientCompany}` : ""}.\n\n${amountBlock}\n\nNous restons à votre disposition pour toute question.\n\nCordialement,\n${coName}`;
}

export function InvoicePreviewSendDialog({ open, onOpenChange, doc, onSent }) {
  const [client, setClient] = useState(null);
  const [settings, setSettings] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, initialData: null });
  const [downloading, setDownloading] = useState(false);
  // La facture naît avec un numéro provisoire : l'émission n'a lieu qu'au moment de l'envoi.
  const [emitted, setEmitted] = useState(null);
  const current = emitted || doc;

  useEffect(() => {
    if (!open || !doc) return;
    setEmitted(null);
    adminAPI.getSettings().then(r => setSettings(r.data || {})).catch(() => setSettings({}));
    if (doc.client_id) {
      clientAPI.get(doc.client_id).then(r => setClient(r.data)).catch(() => setClient(null));
    }
  }, [open, doc]);

  const previewUrl = current?.id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/documents/${current.id}/preview`
    : "";

  const openEmail = useCallback(async () => {
    if (!doc || !client) { toast.error("Données client en cours de chargement..."); return; }
    let d = emitted || doc;
    // Envoyer, c'est émettre : le numéro définitif est attribué AVANT de composer l'email,
    // sinon le sujet et le message porteraient le numéro provisoire.
    if (d.number_is_temp) {
      if (!window.confirm(`L'envoi va émettre cette facture et remplacer le numéro provisoire ${d.number} par son numéro définitif. Cette action est irréversible. Continuer ?`)) return;
      try {
        const res = await documentAPI.emit(d.id);
        d = { ...d, number: res.data.number, number_is_temp: false, status: res.data.status };
        setEmitted(d);
        toast.success(`Facture émise : ${res.data.number}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Émission impossible");
        return;
      }
    }
    const companyName = settings?.name || "";
    const emails = client?.emails || [];
    const defaultEmail = emails[0] || client?.email || "";
    setEmailDialog({
      open: true,
      initialData: {
        email: defaultEmail,
        clientEmails: emails,
        cc: "",
        bcc: "",
        subject: `Facture ${d.number} - ${companyName}`,
        message: buildEmailMessage(d, client, settings),
        docNumber: d.number,
      },
    });
  }, [doc, client, settings, emitted]);

  const handleSend = async ({ email, cc, bcc, subject, message }) => {
    if (!email) { toast.error("Email requis"); return; }
    try {
      const res = await pdfAPI.sendEmail(current.id, email, { cc, bcc, subject, message });
      if (res.data?.status === "error") { toast.error(res.data.message || "Erreur envoi"); return; }
      toast.success(res.data?.message || "Email envoyé");
      if (res.data?.warning) toast.warning(res.data.warning, { duration: 12000 });
      setEmailDialog({ open: false, initialData: null });
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi");
    }
  };

  const handleDownload = async () => {
    if (!doc?.id) return;
    setDownloading(true);
    try {
      const res = await pdfAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${current.number || current.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  const handleCancel = async () => {
    if (!doc?.id) { onOpenChange(false); return; }
    try {
      await billingAPI.cancelInvoice(doc.id);
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  const handleValidate = () => {
    // Garde la facture telle quelle (brouillon, numéro provisoire) et ferme le dialog.
    // L'utilisateur pourra l'émettre ou l'envoyer plus tard depuis la liste des factures.
    toast.success(`Facture ${current?.number || ""} validée`);
    onOpenChange(false);
    onSent?.();
  };

  if (!doc) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="invoice-preview-send-dialog">
          <DialogHeader className="px-5 pt-4 pb-2 border-b border-slate-200 shrink-0">
            <DialogTitle className="flex items-center justify-between gap-3">
              <span className="text-base font-semibold text-slate-800">
                Facture{" "}
                <span
                  className={`font-mono ${current.number_is_temp ? "text-amber-700" : "text-indigo-600"}`}
                  title={current.number_is_temp ? "Numéro provisoire : le numéro définitif sera attribué à l'envoi" : undefined}
                  data-testid="invoice-preview-number"
                >{current.number}</span> créée — Aperçu avant envoi
              </span>
              <span className="text-sm font-medium text-slate-500 money-value">{formatEur(current.total_ttc)}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100">
            {previewUrl ? (
              <iframe
                key={current.number}
                src={previewUrl}
                title={`Aperçu facture ${current.number}`}
                className="w-full h-full border-0"
                data-testid="invoice-preview-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400 text-sm">Aperçu indisponible</div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 bg-white shrink-0">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={!!emitted}
                title={emitted ? "Facture déjà émise : son numéro appartient à la série légale, elle ne peut plus être annulée" : undefined}
                data-testid="invoice-preview-cancel-btn"
              >
                <Ban size={14} className="mr-1.5" />
                Annuler
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading} data-testid="invoice-preview-download-btn">
                {downloading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
                Télécharger PDF
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleValidate}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="invoice-preview-validate-btn"
              >
                <Check size={14} className="mr-1.5" /> Valider
              </Button>
              <Button
                size="sm"
                onClick={openEmail}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="invoice-preview-send-email-btn"
              >
                <Mail size={14} className="mr-1.5" /> Valider et envoyer par email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EmailSendDialog
        open={emailDialog.open}
        onOpenChange={(v) => setEmailDialog((p) => ({ ...p, open: v }))}
        initialData={emailDialog.initialData}
        onSend={handleSend}
      />
    </>
  );
}
